/**
 * The share image (DECISIONS 2026-10-03 "A spoiler-free share card"): 1080×1350, drawn on a canvas in the browser
 * with the game's own fonts, theme colours and base map. The text comes from cardText() (score only); the trail
 * on the map is made up, the same one as the link preview (scripts/og-image.mjs), never the day's.
 */
import type { CardText } from "../game/card";
import { decodeTopo, type Layers, type Topology } from "../map/topo";
import { X, Y } from "../map/projection";

export const CARD_W = 1080;
export const CARD_H = 1350;

/** A made-up trail of footsteps (not a puzzle route): Canaries → Lisbon → London → Venice → Tunis. */
const TRAIL: [number, number][] = [
  [27.95, -15.6],
  [38.72, -9.14],
  [51.5, -0.13],
  [45.44, 12.33],
  [36.8, 10.18],
];

let base: Promise<Layers | null> | null = null;
const loadBase = (): Promise<Layers | null> =>
  (base ??= fetch(`${import.meta.env.BASE_URL}data/base.json`)
    .then((r) => r.json())
    .then((j) => decodeTopo(j as Topology))
    .catch(() => null));

const css = (k: string): string => getComputedStyle(document.documentElement).getPropertyValue(`--${k}`).trim();

export async function renderCard(text: CardText): Promise<Blob> {
  const display = css("display") || "serif";
  const sans = css("sans") || "sans-serif";
  await Promise.all(
    [`400 120px ${display}`, `italic 400 64px ${display}`, `600 150px ${sans}`, `500 44px ${sans}`, `400 40px ${sans}`].map((f) => document.fonts.load(f).catch(() => [])),
  );
  const layers = await loadBase();
  const c = document.createElement("canvas");
  c.width = CARD_W;
  c.height = CARD_H;
  const g = c.getContext("2d")!;
  const ink = css("ink");
  const ink2 = css("ink-2");
  const accent = css("accent");

  g.fillStyle = css("paper");
  g.fillRect(0, 0, CARD_W, CARD_H);

  // The map band (rounded, inset): the made-up trail, fitted with room for its pins.
  const left = 60;
  const top = 300;
  const w = CARD_W - 2 * left;
  const h = 500;
  const pad = 56;
  const xs = TRAIL.map(([, lng]) => X(lng));
  const ys = TRAIL.map(([lat]) => Y(lat));
  const k = Math.min((w - 2 * pad) / (Math.max(...xs) - Math.min(...xs)), (h - 2 * pad) / (Math.max(...ys) - Math.min(...ys)));
  const ox = left + w / 2 - ((Math.min(...xs) + Math.max(...xs)) / 2) * k;
  const oy = top + h / 2 - ((Math.min(...ys) + Math.max(...ys)) / 2) * k;
  g.save();
  g.beginPath();
  g.roundRect(left, top, w, h, 28);
  g.clip();
  g.fillStyle = css("sea");
  g.fillRect(left, top, w, h);
  if (layers?.land) {
    g.save();
    g.setTransform(k, 0, 0, k, ox, oy);
    g.fillStyle = css("land");
    g.fill(layers.land);
    g.lineWidth = 1.4 / k;
    g.strokeStyle = css("coast");
    g.stroke(layers.land);
    g.restore();
  }
  const pts = TRAIL.map(([lat, lng]) => [X(lng) * k + ox, Y(lat) * k + oy] as const);
  g.setLineDash([16, 14]);
  g.lineWidth = 6;
  g.lineCap = "round";
  g.strokeStyle = accent;
  g.beginPath();
  pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
  g.stroke();
  g.setLineDash([]);
  pts.forEach(([x, y], i) => {
    g.beginPath();
    g.arc(x, y, 26, 0, Math.PI * 2);
    g.fillStyle = accent;
    g.fill();
    g.lineWidth = 5;
    g.strokeStyle = css("paper");
    g.stroke();
    g.fillStyle = css("accent-ink");
    g.font = `500 28px ${sans}`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText(String(i + 1), x, y + 1);
  });
  g.restore();
  g.lineWidth = 2;
  g.strokeStyle = css("rule");
  g.beginPath();
  g.roundRect(left, top, w, h, 28);
  g.stroke();

  // Wordmark and puzzle line.
  g.textAlign = "center";
  g.textBaseline = "alphabetic";
  g.font = `400 120px ${display}`;
  const who = "Who";
  const steps = "Steps";
  const ww = g.measureText(who).width;
  const sw = g.measureText(steps).width;
  const x0 = (CARD_W - ww - sw) / 2;
  g.textAlign = "left";
  g.fillStyle = ink;
  g.fillText(who, x0, 175);
  g.fillStyle = accent;
  g.fillText(steps, x0 + ww, 175);
  g.textAlign = "center";
  g.fillStyle = ink2;
  g.font = `500 44px ${sans}`;
  g.fillText(text.puzzle, CARD_W / 2, 255);

  // Score, the three rounds, the "Whose steps?" place.
  g.fillStyle = ink;
  g.font = `600 150px ${sans}`;
  g.fillText(text.score, CARD_W / 2, 960);
  g.font = `400 40px ${sans}`;
  text.rows.forEach((r, i) => {
    const y = 1030 + i * 52;
    g.textAlign = "left";
    g.fillStyle = ink2;
    g.fillText(r.label, 250, y);
    g.textAlign = "right";
    g.fillStyle = ink;
    g.fillText(r.points, CARD_W - 250, y);
  });
  g.textAlign = "center";
  g.fillStyle = ink2;
  g.fillText(text.named, CARD_W / 2, 1196);

  // Call to action.
  g.fillStyle = ink;
  g.font = `italic 400 64px ${display}`;
  g.fillText(text.cta, CARD_W / 2, 1262);
  g.fillStyle = accent;
  g.font = `500 36px ${sans}`;
  g.fillText(text.url, CARD_W / 2, 1318);

  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob"))), "image/png"));
}
